import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Mic, MicOff, Volume2, VolumeX, X, Phone, PhoneOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Detect if Speech Recognition is supported
const isSpeechRecognitionSupported = () => {
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

export default function SakuraChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis Sakura, ton assistante virtuelle. Comment puis-je t'aider aujourd'hui ?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [statusText, setStatusText] = useState("En ligne");
  const [hasTestedVoice, setHasTestedVoice] = useState(false);
  const [micSupported, setMicSupported] = useState(true);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const conversationModeRef = useRef(false);
  const isProcessingRef = useRef(false);

  // Sync ref with state
  useEffect(() => {
    conversationModeRef.current = conversationMode;
  }, [conversationMode]);

  // Check mic support
  useEffect(() => {
    if (!isSpeechRecognitionSupported()) {
      setMicSupported(false);
    }
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSpeechRecognitionSupported()) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'fr-FR';

    recognition.onstart = () => {
      setIsListening(true);
      setStatusText("🎤 Je t'écoute...");
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      console.log("📝 Transcription:", transcript);
      setIsListening(false);
      
      if (transcript.trim() && conversationModeRef.current) {
        handleSendMessage(transcript.trim());
      } else if (transcript.trim()) {
        setInput(transcript);
      }
    };

    recognition.onerror = (event) => {
      console.log("❌ Erreur micro:", event.error);
      setIsListening(false);
      
      if (conversationModeRef.current && event.error !== 'aborted') {
        setTimeout(() => {
          if (conversationModeRef.current && !isProcessingRef.current) {
            startListening();
          }
        }, 1000);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) {}
      }
    };
  }, []);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Simple speak function - same approach as testVoice
  const speak = useCallback((text) => {
    return new Promise((resolve) => {
      if (!voiceEnabled) {
        resolve();
        return;
      }
      
      const synth = window.speechSynthesis;
      synth.cancel();
      
      // Clean emojis
      const cleanText = text
        .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
        .replace(/[\u{2600}-\u{27BF}]/gu, '')
        .replace(/[\u{FE0F}]/gu, '')
        .replace(/[😊😅😬😉😄🌸🌼🎉👉👔🎁💳💵🏠📞📧🌐📱🎤💬🔊🐑🦋✨💧🧺🏷️📸🧪💪🛏️🌟⚠️]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      if (!cleanText) {
        resolve();
        return;
      }

      console.log("🔊 Parole:", cleanText.substring(0, 40) + "...");

      const utterance = new SpeechSynthesisUtterance(cleanText);
      const voices = synth.getVoices();
      
      let frVoice = voices.find(v => v.lang === 'fr-FR' && v.name.toLowerCase().includes('google'));
      if (!frVoice) frVoice = voices.find(v => v.lang === 'fr-FR');
      if (!frVoice) frVoice = voices.find(v => v.lang.startsWith('fr'));
      
      if (frVoice) {
        utterance.voice = frVoice;
      }
      
      utterance.lang = 'fr-FR';
      utterance.volume = 1;
      utterance.rate = 1;
      utterance.pitch = 1.1;
      
      let resolved = false;
      const done = () => {
        if (!resolved) {
          resolved = true;
          setIsSpeaking(false);
          resolve();
        }
      };
      
      utterance.onstart = () => {
        setIsSpeaking(true);
        setStatusText("💬 Je parle...");
      };
      
      utterance.onend = done;
      utterance.onerror = done;
      
      synth.speak(utterance);
      
      // Timeout de sécurité
      setTimeout(done, Math.max(5000, cleanText.length * 80));
    });
  }, [voiceEnabled]);

  // Test voice
  const testVoice = () => {
    const synth = window.speechSynthesis;
    synth.cancel();
    
    const utterance = new SpeechSynthesisUtterance("Bonjour, je suis Sakura !");
    const voices = synth.getVoices();
    
    let frVoice = voices.find(v => v.lang === 'fr-FR' && v.name.toLowerCase().includes('google'));
    if (!frVoice) frVoice = voices.find(v => v.lang.startsWith('fr'));
    
    if (frVoice) utterance.voice = frVoice;
    utterance.lang = 'fr-FR';
    utterance.volume = 1;
    
    utterance.onstart = () => setHasTestedVoice(true);
    synth.speak(utterance);
  };

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current) return;
    
    window.speechSynthesis.cancel();
    setIsSpeaking(false);

    try {
      recognitionRef.current.abort();
    } catch (e) {}
    
    setTimeout(() => {
      try {
        recognitionRef.current.start();
      } catch (e) {}
    }, 100);
  }, []);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) {}
    }
    setIsListening(false);
  }, []);

  // Send message
  const handleSendMessage = useCallback(async (text) => {
    if (!text.trim() || isProcessingRef.current) return;
    
    isProcessingRef.current = true;
    const userMessage = text.trim();
    
    setInput("");
    setIsListening(false);
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);
    setStatusText("🤔 Je réfléchis...");

    let apiSuccess = false;
    let assistantMessage = "";

    // Get customer email if logged in (from localStorage or cookie)
    const customerEmail = localStorage.getItem('customer_email');

    try {
      const response = await fetch(`${BACKEND_URL}/api/chatbot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          message: userMessage, 
          session_id: sessionId,
          customer_email: customerEmail
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        assistantMessage = data.response;
        setMessages(prev => [...prev, { role: "assistant", content: assistantMessage }]);
        apiSuccess = true;
      } else {
        throw new Error("API error");
      }
    } catch (error) {
      console.error("API error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Désolée, j'ai un petit souci technique. Peux-tu réessayer ?" 
      }]);
    }
    
    setIsLoading(false);
    
    // Parler (séparé pour ne pas affecter le message d'erreur)
    if (apiSuccess && assistantMessage) {
      try {
        await speak(assistantMessage);
      } catch (e) {
        console.log("Speak error:", e);
      }
    }
    
    // Mode conversation: réactiver le micro
    if (conversationModeRef.current) {
      setStatusText("🎤 À toi...");
      setTimeout(() => {
        if (conversationModeRef.current) startListening();
      }, 500);
    } else {
      setStatusText("En ligne");
    }
    
    isProcessingRef.current = false;
  }, [sessionId, speak, startListening]);

  // Toggle conversation mode
  const toggleConversationMode = () => {
    if (conversationMode) {
      setConversationMode(false);
      stopListening();
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setStatusText("En ligne");
    } else {
      setConversationMode(true);
      setVoiceEnabled(true);
      setStatusText("🎤 Je t'écoute...");
      setTimeout(() => startListening(), 300);
    }
  };

  // Toggle mic
  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Form submit
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (input.trim()) await handleSendMessage(input);
  };

  // Close
  const closeChat = () => {
    setIsOpen(false);
    window.speechSynthesis.cancel();
    stopListening();
    setConversationMode(false);
    setIsSpeaking(false);
    setStatusText("En ligne");
  };

  return (
    <div className="sakura-chatbot" data-testid="sakura-chatbot">
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-pink-200">
          {/* Header */}
          <div className={`p-4 flex items-center justify-between ${conversationMode ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-pink-500 to-rose-500'}`}>
            <div className="flex items-center gap-3">
              <div className={`relative ${isSpeaking ? 'animate-pulse' : ''}`}>
                <img src="/sakura-avatar.png" alt="Sakura" className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                {(isSpeaking || isListening) && (
                  <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white animate-pulse ${isListening ? 'bg-red-400' : 'bg-green-400'}`} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-white">Sakura</h3>
                <p className="text-white/80 text-xs">{statusText}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {micSupported && (
                <Button size="icon" variant="ghost" onClick={toggleConversationMode}
                  className={`text-white hover:bg-white/20 h-8 w-8 ${conversationMode ? 'bg-white/30' : ''}`}>
                  {conversationMode ? <PhoneOff className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => setVoiceEnabled(!voiceEnabled)}
                className="text-white hover:bg-white/20 h-8 w-8">
                {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={closeChat} className="text-white hover:bg-white/20 h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Banners */}
          {conversationMode && (
            <div className="bg-green-50 border-b border-green-200 px-4 py-2 text-center">
              <p className="text-green-700 text-xs font-medium">📞 Appel en cours — Parle naturellement !</p>
            </div>
          )}
          {!hasTestedVoice && !conversationMode && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
              <p className="text-amber-700 text-xs">🔊 Clique pour activer la voix</p>
              <Button size="sm" variant="outline" onClick={testVoice} className="h-6 text-xs border-amber-300 text-amber-700 hover:bg-amber-100">
                Tester
              </Button>
            </div>
          )}
          {!micSupported && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-center">
              <p className="text-blue-700 text-xs">📱 Sur iPhone, le micro n'est pas disponible</p>
            </div>
          )}

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-pink-50 to-white">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  msg.role === "user" ? "bg-pink-500 text-white rounded-br-sm" : "bg-white border border-pink-100 text-slate-700 rounded-bl-sm shadow-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-pink-100 rounded-2xl px-4 py-2 shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            {isListening && (
              <div className="flex justify-end">
                <div className="bg-red-100 border border-red-200 rounded-2xl px-4 py-2 text-sm text-red-700 animate-pulse">
                  🎤 Écoute...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 bg-white border-t border-pink-100">
            <div className="flex gap-2">
              {micSupported && (
                <Button type="button" size="icon" variant={isListening ? "default" : "outline"}
                  onClick={toggleListening} disabled={conversationMode || isLoading}
                  className={`shrink-0 ${isListening ? "bg-red-500 hover:bg-red-600" : "border-pink-300 text-pink-600 hover:bg-pink-50"}`}>
                  {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              )}
              <Input value={input} onChange={(e) => setInput(e.target.value)}
                placeholder={conversationMode ? "Mode vocal actif..." : "Pose-moi une question..."}
                className="flex-1 border-pink-200" disabled={isLoading || conversationMode} />
              <Button type="submit" size="icon" disabled={!input.trim() || isLoading || conversationMode}
                className="shrink-0 bg-pink-500 hover:bg-pink-600">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Toggle Button */}
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="group relative" data-testid="sakura-chatbot-toggle">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-pink-400 shadow-lg transition-transform group-hover:scale-105 bg-white">
              <img src="/sakura-avatar.png" alt="Sakura" className="w-full h-full object-cover" />
            </div>
            <span className="absolute bottom-1 right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white" />
            <div className="absolute -top-2 -right-2 bg-pink-500 text-white rounded-full p-1.5 shadow-md">
              <MessageCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Besoin d'aide ?
          </div>
        </button>
      )}
    </div>
  );
}
